/**
 * Admin Devices page (Requirement 16).
 *
 * Wraps the existing `/devices` admin endpoints (Task 9.2) so an admin
 * Operator can register a Capture_Device, see the list of registered
 * devices, and revoke one — without hand-crafting curl + a Cognito ID
 * token.
 *
 * Three surfaces:
 *   1. "Register new device" form → POST /devices.
 *   2. Registration-success modal — the one place the freshly-minted
 *      X.509 cert + private key are visible. The modal renders a
 *      "Download credential bundle" button that produces a single
 *      .zip containing device.pem.crt, device.pem.key, AmazonRootCA1.pem,
 *      and a pre-populated firmware.env. The credential material lives
 *      in modal-scoped React state only — the SPA never writes it to
 *      localStorage / sessionStorage.
 *   3. Registered devices table — backed by GET /devices. Each ACTIVE
 *      row exposes a "Revoke" button which calls DELETE /devices/{id}
 *      after a confirmation modal.
 *
 * Validates: Requirement 16
 */

import { useCallback, useEffect, useState } from 'react';
import type { DeviceRegistryEntry } from '@tcvs/shared';
import {
  buildFirmwareEnv,
  useDevicesApi,
  type RegisterDeviceInput,
  type RegisterDeviceResponse,
} from './devices-api';

interface PageError {
  message: string;
  /** Failing API request id, when present. Useful for log lookups. */
  requestId?: string;
}

export function DevicesPage(): JSX.Element {
  const api = useDevicesApi();

  // Devices list state.
  const [devices, setDevices] = useState<DeviceRegistryEntry[]>([]);
  const [listing, setListing] = useState(false);
  const [error, setError] = useState<PageError | null>(null);

  // Register-form state.
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [registering, setRegistering] = useState(false);
  const [credential, setCredential] = useState<RegisterDeviceResponse | null>(null);

  // Revoke-modal state.
  const [revokeTarget, setRevokeTarget] = useState<DeviceRegistryEntry | null>(null);
  const [revoking, setRevoking] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setListing(true);
    const result = await api.list();
    setListing(false);
    if (!result.ok) {
      setError(toPageError('list', result));
      return;
    }
    setDevices(result.data.items);
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onRegister = useCallback(
    async (input: RegisterDeviceInput) => {
      setError(null);
      setRegistering(true);
      const result = await api.register(input);
      setRegistering(false);
      if (!result.ok) {
        setError(toPageError('register', result));
        return;
      }
      setCredential(result.data);
      setName('');
      setLocation('');
      void refresh();
    },
    [api, refresh],
  );

  const onConfirmRevoke = useCallback(async () => {
    if (revokeTarget === null) return;
    setError(null);
    setRevoking(true);
    const result = await api.revoke(revokeTarget.deviceId);
    setRevoking(false);
    setRevokeTarget(null);
    if (!result.ok) {
      setError(toPageError('revoke', result));
      return;
    }
    void refresh();
  }, [api, refresh, revokeTarget]);

  return (
    <div>
      <h2>Devices</h2>
      <p>
        Register a Capture_Device and download its credential bundle. The private key is shown
        exactly once at registration time and cannot be retrieved again.
      </p>

      {error !== null ? (
        <div
          role="alert"
          className="tcvs-error"
          style={{
            border: '1px solid var(--tcvs-danger-border)',
            background: 'var(--tcvs-danger-bg)',
            padding: 12,
            marginBottom: 16,
          }}
        >
          <strong>Error:</strong> {error.message}
          {error.requestId !== undefined ? (
            <span className="tcvs-muted" style={{ marginLeft: 8 }}>
              (request {error.requestId})
            </span>
          ) : null}
        </div>
      ) : null}

      <RegisterForm
        name={name}
        location={location}
        registering={registering}
        onNameChange={setName}
        onLocationChange={setLocation}
        onSubmit={() => {
          const input: RegisterDeviceInput = {};
          if (name !== '') input.name = name;
          if (location !== '') input.location = location;
          void onRegister(input);
        }}
      />

      <DevicesTable
        devices={devices}
        listing={listing}
        onRevoke={(d) => setRevokeTarget(d)}
      />

      {credential !== null ? (
        <RegistrationSuccessModal
          credential={credential}
          onDismiss={() => setCredential(null)}
        />
      ) : null}

      {revokeTarget !== null ? (
        <RevokeConfirmModal
          target={revokeTarget}
          revoking={revoking}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => void onConfirmRevoke()}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Register form
// ---------------------------------------------------------------------------

interface RegisterFormProps {
  name: string;
  location: string;
  registering: boolean;
  onNameChange: (v: string) => void;
  onLocationChange: (v: string) => void;
  onSubmit: () => void;
}

function RegisterForm({
  name,
  location,
  registering,
  onNameChange,
  onLocationChange,
  onSubmit,
}: RegisterFormProps): JSX.Element {
  return (
    <section
      className="tcvs-card"
      style={{
        marginBottom: 24,
      }}
    >
      <h3 style={{ marginTop: 0 }}>Register new device</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!registering) onSubmit();
        }}
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}
      >
        <label className="tcvs-field">
          <span>Name (optional)</span>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="imager1"
          />
        </label>
        <label className="tcvs-field">
          <span>Location (optional)</span>
          <input
            type="text"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            placeholder="workshop"
          />
        </label>
        <button type="submit" disabled={registering}>
          {registering ? 'Registering…' : 'Register'}
        </button>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Devices table
// ---------------------------------------------------------------------------

interface DevicesTableProps {
  devices: readonly DeviceRegistryEntry[];
  listing: boolean;
  onRevoke: (d: DeviceRegistryEntry) => void;
}

function DevicesTable({ devices, listing, onRevoke }: DevicesTableProps): JSX.Element {
  if (listing && devices.length === 0) {
    return <p className="tcvs-muted">Loading devices…</p>;
  }
  if (devices.length === 0) {
    return <p className="tcvs-muted">No devices registered yet.</p>;
  }
  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
      <thead>
        <tr style={{ background: 'var(--tcvs-th-bg)' }}>
          <th style={cellStyle}>Device ID</th>
          <th style={cellStyle}>Status</th>
          <th style={cellStyle}>Name</th>
          <th style={cellStyle}>Location</th>
          <th style={cellStyle}>Issued</th>
          <th style={cellStyle}>Revoked</th>
          <th style={cellStyle}>IoT Thing</th>
          <th style={cellStyle}>Cert ID</th>
          <th style={cellStyle}></th>
        </tr>
      </thead>
      <tbody>
        {devices.map((d) => (
          <tr key={d.deviceId}>
            <td style={cellStyle}>
              <CopyableId value={d.deviceId} />
            </td>
            <td style={cellStyle}>
              <StatusPill status={d.status} />
            </td>
            <td style={cellStyle}>{d.metadata?.name ?? ''}</td>
            <td style={cellStyle}>{d.metadata?.location ?? ''}</td>
            <td style={cellStyle}>{formatTimestamp(d.issuedAt)}</td>
            <td style={cellStyle}>{d.revokedAt !== null ? formatTimestamp(d.revokedAt) : ''}</td>
            <td style={cellStyle}>
              <CopyableId value={d.iotThingName} />
            </td>
            <td style={cellStyle}>
              <CopyableId value={d.certificateId} />
            </td>
            <td style={cellStyle}>
              {d.status === 'ACTIVE' ? (
                <button type="button" onClick={() => onRevoke(d)}>
                  Revoke
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--tcvs-border)',
  textAlign: 'left',
  verticalAlign: 'top',
};

function StatusPill({ status }: { status: 'ACTIVE' | 'REVOKED' }): JSX.Element {
  const isActive = status === 'ACTIVE';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 999,
        background: isActive ? 'var(--tcvs-success-bg)' : 'var(--tcvs-danger-bg)',
        color: isActive ? 'var(--tcvs-success)' : 'var(--tcvs-danger)',
      }}
    >
      {status}
    </span>
  );
}

function CopyableId({ value }: { value: string }): JSX.Element {
  const truncated = value.length > 12 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;
  return (
    <code
      title={value}
      className="tcvs-mono"
      onClick={() => {
        if (navigator.clipboard !== undefined) void navigator.clipboard.writeText(value);
      }}
      style={{ cursor: 'copy' }}
    >
      {truncated}
    </code>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// ---------------------------------------------------------------------------
// Registration-success modal
// ---------------------------------------------------------------------------

interface RegistrationSuccessModalProps {
  credential: RegisterDeviceResponse;
  onDismiss: () => void;
}

function RegistrationSuccessModal({
  credential,
  onDismiss,
}: RegistrationSuccessModalProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const onDownload = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await buildCredentialBundle(credential);
      triggerDownload(blob, `tcvs-${credential.deviceId}.zip`);
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : String(err));
    } finally {
      setDownloading(false);
    }
  }, [credential]);

  return (
    <ModalShell onDismiss={onDismiss}>
      <h3 style={{ marginTop: 0 }}>Device registered</h3>
      <div
        style={{
          background: 'var(--tcvs-warning-bg)',
          border: '1px solid var(--tcvs-warning-border)',
          padding: 8,
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        <strong>Save the credential bundle now.</strong> The private key below is shown exactly
        once and cannot be retrieved again. If you lose it, you must revoke this device and
        register a new one.
      </div>

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
        <dt>Device ID</dt>
        <dd className="tcvs-mono" style={{ margin: 0 }}>{credential.deviceId}</dd>
        <dt>IoT endpoint</dt>
        <dd className="tcvs-mono" style={{ margin: 0 }}>{credential.iotEndpoint}</dd>
        <dt>Root CA URL</dt>
        <dd className="tcvs-mono" style={{ margin: 0 }}>
          <a href={credential.rootCaUrl} target="_blank" rel="noreferrer">
            {credential.rootCaUrl}
          </a>
        </dd>
      </dl>

      <details style={{ marginTop: 12 }}>
        <summary>Show certificate (PEM)</summary>
        <pre style={pemStyle}>{credential.certificate}</pre>
      </details>
      <details>
        <summary>Show private key (PEM)</summary>
        <pre style={pemStyle}>{credential.privateKey}</pre>
      </details>

      <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={() => void onDownload()} disabled={downloading}>
          {downloading ? 'Building…' : 'Download credential bundle (.zip)'}
        </button>
        <button type="button" onClick={onDismiss}>
          Done — discard from page
        </button>
      </div>
      {downloadError !== null ? (
        <p className="tcvs-error" style={{ marginTop: 8 }}>Download error: {downloadError}</p>
      ) : null}
    </ModalShell>
  );
}

const pemStyle: React.CSSProperties = {
  background: 'var(--tcvs-surface-alt)',
  border: '1px solid var(--tcvs-border)',
  padding: 8,
  fontSize: 11,
  overflowX: 'auto',
  maxHeight: 200,
};

// ---------------------------------------------------------------------------
// Revoke confirmation modal
// ---------------------------------------------------------------------------

interface RevokeConfirmModalProps {
  target: DeviceRegistryEntry;
  revoking: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function RevokeConfirmModal({
  target,
  revoking,
  onCancel,
  onConfirm,
}: RevokeConfirmModalProps): JSX.Element {
  return (
    <ModalShell onDismiss={onCancel}>
      <h3 style={{ marginTop: 0 }}>Revoke device?</h3>
      <p>
        Revoke <code>{target.deviceId}</code>? The device's X.509 certificate will be marked
        INACTIVE in IoT Core. Subsequent submissions from this device will be rejected within 5
        minutes (Requirement 8.7).
      </p>
      {target.metadata?.name !== undefined ? (
        <p className="tcvs-muted">
          Name: <strong>{target.metadata.name}</strong>
          {target.metadata.location !== undefined ? (
            <>
              {' '}· Location: <strong>{target.metadata.location}</strong>
            </>
          ) : null}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={onCancel} disabled={revoking}>
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={revoking}
          className="tcvs-btn-danger"
        >
          {revoking ? 'Revoking…' : 'Revoke device'}
        </button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------

interface ModalShellProps {
  onDismiss: () => void;
  children: React.ReactNode;
}

function ModalShell({ onDismiss, children }: ModalShellProps): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--tcvs-surface)',
          padding: 16,
          borderRadius: 6,
          maxWidth: 720,
          width: '90vw',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ApiFailureLike {
  ok: false;
  error: string;
  message?: string;
  body?: unknown;
}

function toPageError(action: string, failure: ApiFailureLike): PageError {
  const errBody = (failure.body ?? {}) as { requestId?: string };
  const fallbackMsg = `${action} failed: ${failure.message ?? failure.error}`;
  const result: PageError = { message: fallbackMsg };
  if (errBody.requestId !== undefined) result.requestId = errBody.requestId;
  return result;
}

/**
 * Build a single .zip download containing the four files the operator
 * needs to bring up a fresh Pi:
 *   - device.pem.crt          → cert
 *   - device.pem.key          → key
 *   - AmazonRootCA1.pem       → AWS root CA (fetched at click time)
 *   - firmware.env            → dotenv snippet pre-populated for /home/pi/firmware
 *
 * Lazy-imports `jszip` so the main SPA bundle stays small — the dep
 * only loads when the operator actually clicks "Download".
 */
async function buildCredentialBundle(credential: RegisterDeviceResponse): Promise<Blob> {
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  zip.file('device.pem.crt', credential.certificate);
  zip.file('device.pem.key', credential.privateKey);
  zip.file('firmware.env', buildFirmwareEnv(credential));
  // Best-effort fetch of the root CA so the operator gets a single,
  // self-contained bundle. If the fetch fails (offline / CORS) we
  // include a small README pointing at the URL instead so the bundle
  // is still useful.
  try {
    const response = await fetch(credential.rootCaUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const text = await response.text();
    zip.file('AmazonRootCA1.pem', text);
  } catch (err) {
    zip.file(
      'README-root-ca.txt',
      [
        'AmazonRootCA1.pem could not be fetched at bundle time:',
        `  ${err instanceof Error ? err.message : String(err)}`,
        '',
        'Download it manually from:',
        `  ${credential.rootCaUrl}`,
        'and save it to /home/pi/firmware/certs/AmazonRootCA1.pem on the Pi.',
        '',
      ].join('\n'),
    );
  }
  return zip.generateAsync({ type: 'blob' });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the click handler has flushed.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

import type * as React from 'react';
